// assignee-workload-chart.tsx — Assignee open ticket workload stacked bar chart.
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { AssigneeWorkload } from '@/api/tickets-statistics';

const SERIES = [
  { key: 'high_priority', label: 'High',   color: CHART_COLORS[4] }, // red
  { key: 'medium_priority', label: 'Medium', color: CHART_COLORS[1] }, // orange
  { key: 'low_priority',  label: 'Low',    color: CHART_COLORS[2] }, // green
];

interface Props {
  data: AssigneeWorkload[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function AssigneeWorkloadChart({ data, isLoading, isError }: Props) {
  const rows = (data ?? [])
    .filter((d) => d.open_tickets > 0)
    .sort((a, b) => b.open_tickets - a.open_tickets)
    .slice(0, 15)
    .map((d) => ({
      label: d.employee_name,
      high_priority:   d.high_priority,
      medium_priority: d.medium_priority,
      low_priority:    d.low_priority,
    }));

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Assignee Workload (Open Tickets)</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load workload data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 260 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No open tickets" description="No assignee workload data in this date range." />
        ) : (
          <Chart
            type="bar"
            stacked
            data={rows}
            xKey="label"
            series={SERIES}
            height={260}
            showGrid
            showXAxis
            showYAxis
            valueFormatter={(v) => String(v ?? 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
